import { PlannerEcsSystem, RangeComponent } from '@inter-ikea-storageone/range';
import { BestaLegsComponent } from '../components/BestaLegsComponent';
import BestaFrameComponent from '../components/BestaFrameComponent';
import { ComponentIds, HierarchyUtils } from '@inter-ikea-core/core';
/**
 * Listens to changes to {@link GuestConnectionsComponent} on an entity and updates whether the entity
 * should have legs or not, and if these legs should be merged or not and set this data in the
 * {@link BestaLegsComponent}
 */
export class BestaFrameLegsCalculatorSystem extends PlannerEcsSystem {
    /**
     *
     * @param coreApi
     * @param applicableFrameComponentId The component tagging a frame as the type that this
     * system should act on. Could be Core's frame component or could be a planner specific component
     */
    constructor(coreApi, applicableFrameComponentId) {
        super(coreApi, 'BestaFrameLegsCalculatorSystem', ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID);
        this.floorConnectorKeys = ['floor-connector-low-legs', 'floor-connector-high-legs'];
        this.wallConnectorKey = 'wall';
        this.leftAlignmentToMergeLegs = 'merge-legs-left';
        this.rightAlignmentToMergeLegs = 'merge-legs-right';
        this.applicableFrameComponentId = applicableFrameComponentId;
        this.coreApi = coreApi;
        this.entityManager = coreApi.getServices().entityManager;
    }
    onAfterLoad(loadedLegType, loadedFrames) {
        // First make sure all frames get a legs component
        loadedFrames.forEach((frame) => {
            this.entityManager.addComponent(frame, new BestaLegsComponent(false, null, null, loadedLegType.legProductRef, loadedLegType.useSupportLeg));
        });
        loadedFrames.forEach((frame) => {
            let connection = frame.getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID);
            // If the frame id not have a connection then it probably is part of an entity group, so get that connection
            if (!connection) {
                const entityGroup = HierarchyUtils.findWithComponentInUpwardsHierarchy(frame, ComponentIds.AUTOMATIC_ENTITY_GROUP_COMPONENT_ID);
                if (entityGroup)
                    connection = entityGroup.getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID);
            }
            if (connection)
                this.calculateFrameLegs(frame, connection);
        });
    }
    onAdd(entity, newConnectionComponent) {
        // Only apply this system to BESTÅ frames
        if (!this.isBestaFrame(entity))
            return;
        this.calculateFrameLegs(entity, newConnectionComponent);
    }
    onUpdate(entity, oldConnectionComponent, newConnectionComponent) {
        if (!this.isBestaFrame(entity))
            return;
        this.calculateFrameLegs(entity, newConnectionComponent);
    }
    onRemove(entity) {
        // Only apply this system to BESTÅ frames
        if (this.isBestaFrame(entity))
            this.unmergeOldNeighbours(entity);
    }
    isBestaFrame(entity) {
        return (entity.hasComponent(this.applicableFrameComponentId) &&
            entity.getComponent(RangeComponent.id)?.range === 'besta');
    }
    calculateFrameLegs(frameEntity, connectionComponent) {
        if (connectionComponent.highestPriorityConnection &&
            this.floorConnectorKeys.includes(connectionComponent.highestPriorityConnection.guestConnector.key)) {
            this.handleConnectedToFloor(frameEntity);
        }
        else if (connectionComponent.highestPriorityConnection &&
            connectionComponent.highestPriorityConnection.guestConnector.key === this.wallConnectorKey)
            this.handleConnectedToWall(frameEntity);
        else
            this.handleConnectedToNothing(frameEntity);
    }
    handleConnectedToWall(frameEntity) {
        this.unmergeOldNeighbours(frameEntity);
        const oldComponent = frameEntity.getComponent(BestaLegsComponent.id);
        if (!oldComponent) {
            new Error(`handleConnectedToWall: Frame ${frameEntity} lost its BestaLegsComponent`);
            return;
        }
        const newComponent = oldComponent.withConnectedToFloor(false);
        this.entityManager.addComponent(frameEntity, newComponent);
        this.updateStackedFrames(frameEntity);
    }
    /**
     * This will handle when frame was connected to floor, will calculate neighbours and
     * also update the neighbour data of those neighbours so they update their state
     * @param frameEntity
     */
    handleConnectedToFloor(frameEntity) {
        this.unmergeOldNeighbours(frameEntity);
        const alignedFrames = this.coreApi.entities.getAlignedFrames(frameEntity, this.leftAlignmentToMergeLegs, this.rightAlignmentToMergeLegs, false, true);
        if (alignedFrames.left) {
            this.mergeLeftNeighbour(frameEntity, alignedFrames.left);
        }
        if (alignedFrames.right) {
            this.mergeRightNeighbour(frameEntity, alignedFrames.right);
        }
        const oldComponent = frameEntity.getComponent(BestaLegsComponent.id);
        if (!oldComponent) {
            new Error(`handleConnectedToFloor: Frame ${frameEntity} lost its BestaLegsComponent`);
            return;
        }
        // Note that we specifically set neighbours here
        const newComponent = new BestaLegsComponent(true, alignedFrames.left, alignedFrames.right, oldComponent.legProductRef, oldComponent.useSupportLeg);
        this.entityManager.addComponent(frameEntity, newComponent);
        this.updateStackedFrames(frameEntity);
    }
    /**
     * Frame is not connected to neither floor or wall. This means it is stacked.
     * It then doesn't need legs but can need suspension rail depending on if bottom
     * frame in stack is floorstanding or not
     * @param frameEntity
     */
    handleConnectedToNothing(frameEntity) {
        const oldComponent = frameEntity.getComponent(BestaLegsComponent.id);
        if (!oldComponent) {
            new Error(`handleConnectedToNothing: Frame ${frameEntity} lost its BestaLegsComponent`);
            return;
        }
        const newComponent = oldComponent.withConnectedToNothing();
        this.entityManager.addComponent(frameEntity, newComponent);
        this.updateStackedFrames(frameEntity);
    }
    updateStackedFrames(bottomEntity) {
        const stackedFrames = HierarchyUtils.descendants(bottomEntity).filter((e) => e.hasComponent(BestaFrameComponent.id));
        stackedFrames.forEach(frame => this.handleConnectedToNothing(frame));
    }
    unmergeOldNeighbours(entity) {
        const entityParent = HierarchyUtils.parent(entity);
        const entityParentIsEntityGroup = entityParent?.hasComponent(ComponentIds.AUTOMATIC_ENTITY_GROUP_COMPONENT_ID);
        // Here we determine if we really should unmerge. Unmerging when not needed causes an extra component update that can create an infinite loop...
        const oldBestaLegsComponent = entity.getComponent(BestaLegsComponent.id);
        if (!oldBestaLegsComponent)
            return;
        if (oldBestaLegsComponent.leftNeighbour) {
            if (entityParent && entityParentIsEntityGroup) {
                const leftNeighbourIsPartOfSameEntityGroup = HierarchyUtils.isDescendantOf(oldBestaLegsComponent.leftNeighbour, entityParent);
                if (!leftNeighbourIsPartOfSameEntityGroup) {
                    this.unmergeLeftNeighbour(entity, oldBestaLegsComponent.leftNeighbour);
                }
            }
            else {
                this.unmergeLeftNeighbour(entity, oldBestaLegsComponent.leftNeighbour);
            }
        }
        if (oldBestaLegsComponent.rightNeighbour) {
            if (entityParent && entityParentIsEntityGroup) {
                const rightNeighbourIsPartOfSameEntityGroup = HierarchyUtils.isDescendantOf(oldBestaLegsComponent.rightNeighbour, entityParent);
                if (!rightNeighbourIsPartOfSameEntityGroup) {
                    this.unmergeRightNeighbour(entity, oldBestaLegsComponent.rightNeighbour);
                }
            }
            else {
                this.unmergeRightNeighbour(entity, oldBestaLegsComponent.rightNeighbour);
            }
        }
    }
    unmergeLeftNeighbour(self, leftNeighbour) {
        const oldSelfFrameComponent = self.getComponent(BestaLegsComponent.id);
        if (leftNeighbour.isDisposed && oldSelfFrameComponent) {
            // If left neighbour is disposed, we reset the left neighbour in self
            const newSelfFrameComponent = oldSelfFrameComponent.withLeftNeighbour(null);
            self.addComponent(newSelfFrameComponent);
            return;
        }
        else {
            const oldLeftFrameComponent = leftNeighbour.getComponent(BestaLegsComponent.id);
            if (!oldLeftFrameComponent || !oldSelfFrameComponent) {
                this.coreApi.reportErrorToSentry(new Error(`unmergeLeftNeighbour: Neighbour ${leftNeighbour} or self ${self} lost its BestaLegsComponent`), { entities: { allEntities: this.coreApi.entities.getAll(), leftNeighbour, self } });
                return;
            }
            // my left neighbour no longer has me as right neighbour
            const newLeftFrameComponent = oldLeftFrameComponent.withRightNeighbour(null);
            leftNeighbour.addComponent(newLeftFrameComponent);
            const newSelfFrameComponent = oldSelfFrameComponent.withLeftNeighbour(null);
            self.addComponent(newSelfFrameComponent);
        }
    }
    unmergeRightNeighbour(self, rightNeighbour) {
        const oldSelfFrameComponent = self.getComponent(BestaLegsComponent.id);
        if (rightNeighbour.isDisposed && oldSelfFrameComponent) {
            // If right neighbour is disposed, we reset the right neighbour in self
            const newSelfFrameComponent = oldSelfFrameComponent.withRightNeighbour(null);
            self.addComponent(newSelfFrameComponent);
            return;
        }
        else {
            const oldRightFrameComponent = rightNeighbour.getComponent(BestaLegsComponent.id);
            if (!oldRightFrameComponent || !oldSelfFrameComponent) {
                this.coreApi.reportErrorToSentry(new Error(`unmergeRightNeighbour: Neighbour ${rightNeighbour} or self ${self} lost its BestaLegsComponent`), { entities: { allEntities: this.coreApi.entities.getAll(), rightNeighbour, self } });
                return;
            }
            // my right neighbour no longer has me as left neighbour
            const newRightFrameComponent = oldRightFrameComponent.withLeftNeighbour(null);
            rightNeighbour.addComponent(newRightFrameComponent);
            const newSelfFrameComponent = oldSelfFrameComponent.withRightNeighbour(null);
            self.addComponent(newSelfFrameComponent);
        }
    }
    mergeLeftNeighbour(entity, leftNeighbour) {
        const oldLeftFrameComponent = leftNeighbour.getComponent(BestaLegsComponent.id);
        if (!oldLeftFrameComponent) {
            this.coreApi.reportErrorToSentry(new Error(`mergeLeftNeighbour: Neighbour ${leftNeighbour} lost its BestaLegsComponent`), { entities: { allEntities: this.coreApi.entities.getAll(), leftNeighbour } });
            return;
        }
        // my left neighbour now has me as right neighbour
        const newLeftFrameComponent = oldLeftFrameComponent.withRightNeighbour(entity);
        this.entityManager.addComponent(leftNeighbour, newLeftFrameComponent);
    }
    mergeRightNeighbour(entity, rightNeighbour) {
        const oldRightFrameComponent = rightNeighbour.getComponent(BestaLegsComponent.id);
        if (!oldRightFrameComponent) {
            this.coreApi.reportErrorToSentry(new Error(`mergeRightNeighbour: Neighbour ${rightNeighbour} lost its BestaLegsComponent`), { entities: { allEntities: this.coreApi.entities.getAll(), rightNeighbour } });
            return;
        }
        // my right neighbour now has me as left neighbour
        const newRightFrameComponent = oldRightFrameComponent.withLeftNeighbour(entity);
        this.entityManager.addComponent(rightNeighbour, newRightFrameComponent);
    }
}
//# sourceMappingURL=BestaFrameLegsCalculatorSystem.js.map